import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type { PurchaseOrder } from "serviceStores/mock/purchase-orders.mock";
import type { PurchaseOrderLineItem } from "serviceStores/mock/purchase-order-line-items.mock";
import type { Plant } from "serviceStores/mock/plants.mock";
import type { UploadedFile } from "@intrasync/react";

/**
 * Receiving line item with received quantity tracking
 */
interface ReceivingLineItem extends PurchaseOrderLineItem {
  receivingQuantity: number;
  damageNotes?: string;
  hasDamage: boolean;
}

/**
 * Form data for PO receiving
 */
interface POReceivingFormData {
  // Step 1: PO Selection
  purchaseOrderId: number | null;
  selectedPO: PurchaseOrder | null;

  // Step 2: Line Items
  lineItems: ReceivingLineItem[];
  receiveAll: boolean;

  // Step 3: Packing Slip
  packingSlipNumber: string;
  packingSlipDate: string;
  documents: UploadedFile[];

  // Step 4: Notes
  receivingNotes: string;
  damageReported: boolean;
  damageDescription: string;
}

/**
 * Initial form data
 */
const initialFormData: POReceivingFormData = {
  purchaseOrderId: null,
  selectedPO: null,
  lineItems: [],
  receiveAll: true,
  packingSlipNumber: "",
  packingSlipDate: new Date().toISOString().split("T")[0],
  documents: [],
  receivingNotes: "",
  damageReported: false,
  damageDescription: "",
};

/**
 * PO Receiving Wizard Store
 *
 * Manages state for the Purchase Order receiving wizard.
 * Allows warehouse/receiving staff to record receipt of goods against POs.
 */
class POReceivingWizardStore {
  rootStore: RootStore;

  // Step navigation
  activeStep = 0;
  readonly steps = [
    "Select PO",
    "Verify Items",
    "Packing Slip",
    "Confirm Receipt",
  ];

  // UI State
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: POReceivingFormData = { ...initialFormData };

  // Reference Data
  plants: Plant[] = [];
  pendingPOs: PurchaseOrder[] = [];

  // Filters
  plantFilter: number | null = null;
  vendorFilter: string = "";
  searchTerm: string = "";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Step Navigation ====================

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  canProceed = (): boolean => {
    return this.validateCurrentStep(false);
  };

  // ==================== Filtered Data ====================

  get filteredPOs(): PurchaseOrder[] {
    let filtered = this.pendingPOs;

    if (this.plantFilter) {
      filtered = filtered.filter((po) => po.plantId === this.plantFilter);
    }

    if (this.vendorFilter) {
      filtered = filtered.filter((po) =>
        po.vendorName?.toLowerCase().includes(this.vendorFilter.toLowerCase())
      );
    }

    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (po) =>
          po.poNumber.toLowerCase().includes(search) ||
          po.vendorName?.toLowerCase().includes(search) ||
          po.jobNumber?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }

  setPlantFilter = (plantId: number | null) => {
    this.plantFilter = plantId;
  };

  setVendorFilter = (vendor: string) => {
    this.vendorFilter = vendor;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  // ==================== PO Selection ====================

  selectPO = async (poId: number) => {
    const po = this.pendingPOs.find((p) => p.purchaseOrderId === poId);
    if (!po) return;

    this.formData.purchaseOrderId = poId;
    this.formData.selectedPO = po;

    // Load line items for this PO
    await this.loadLineItemsForPO(poId);
  };

  loadLineItemsForPO = async (poId: number) => {
    this.loading = true;
    try {
      const lineItemsStore = (this.rootStore as any).purchaseOrderLineItemsStore;
      if (lineItemsStore) {
        await lineItemsStore.find({
          query: { purchaseOrderId: poId },
        });

        runInAction(() => {
          const items = lineItemsStore.entities.filter(
            (item: PurchaseOrderLineItem) => item.purchaseOrderId === poId
          );

          this.formData.lineItems = items.map((item: PurchaseOrderLineItem) => ({
            ...item,
            receivingQuantity: item.quantity - item.receivedQuantity,
            damageNotes: "",
            hasDamage: false,
          }));
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load line items";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  clearSelectedPO = () => {
    this.formData.purchaseOrderId = null;
    this.formData.selectedPO = null;
    this.formData.lineItems = [];
  };

  // ==================== Line Item Management ====================

  updateLineItemQuantity = (lineItemId: number, quantity: number) => {
    const item = this.formData.lineItems.find((i) => i.lineItemId === lineItemId);
    if (item) {
      const maxReceivable = item.quantity - item.receivedQuantity;
      item.receivingQuantity = Math.min(Math.max(0, quantity), maxReceivable);
    }
  };

  toggleLineItemDamage = (lineItemId: number, hasDamage: boolean) => {
    const item = this.formData.lineItems.find((i) => i.lineItemId === lineItemId);
    if (item) {
      item.hasDamage = hasDamage;
      if (!hasDamage) {
        item.damageNotes = "";
      }
    }
  };

  updateLineItemDamageNotes = (lineItemId: number, notes: string) => {
    const item = this.formData.lineItems.find((i) => i.lineItemId === lineItemId);
    if (item) {
      item.damageNotes = notes;
    }
  };

  receiveAllItems = () => {
    this.formData.receiveAll = true;
    this.formData.lineItems.forEach((item) => {
      item.receivingQuantity = item.quantity - item.receivedQuantity;
    });
  };

  clearAllQuantities = () => {
    this.formData.receiveAll = false;
    this.formData.lineItems.forEach((item) => {
      item.receivingQuantity = 0;
    });
  };

  // ==================== Document Management ====================

  addDocuments = (files: UploadedFile[]) => {
    this.formData.documents = [...this.formData.documents, ...files];
  };

  removeDocument = (fileId: string) => {
    this.formData.documents = this.formData.documents.filter(
      (d) => d.id !== fileId
    );
  };

  // ==================== Calculations ====================

  get totalItemsToReceive(): number {
    return this.formData.lineItems.reduce(
      (sum, item) => sum + item.receivingQuantity,
      0
    );
  }

  get totalItemsOnPO(): number {
    return this.formData.lineItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  get receivingValue(): number {
    return this.formData.lineItems.reduce(
      (sum, item) => sum + item.receivingQuantity * item.unitPrice,
      0
    );
  }

  get isPartialReceipt(): boolean {
    return this.formData.lineItems.some(
      (item) => item.receivingQuantity < item.quantity - item.receivedQuantity
    );
  }

  get hasDamagedItems(): boolean {
    return this.formData.lineItems.some((item) => item.hasDamage);
  }

  // ==================== Validation ====================

  validateCurrentStep = (showError = true): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0: // Select PO
        if (!this.formData.purchaseOrderId) {
          if (showError) this.error = "Please select a Purchase Order to receive";
          return false;
        }
        return true;

      case 1: { // Verify Items
        const hasReceivedItems = this.formData.lineItems.some(
          (item) => item.receivingQuantity > 0
        );
        if (!hasReceivedItems) {
          if (showError)
            this.error = "Please enter received quantities for at least one item";
          return false;
        }
        const hasDamageWithoutNotes = this.formData.lineItems.some(
          (item) => item.hasDamage && !item.damageNotes
        );
        if (hasDamageWithoutNotes) {
          if (showError)
            this.error = "Please provide damage notes for all damaged items";
          return false;
        }
        return true;
      }

      case 2: // Packing Slip
        if (!this.formData.packingSlipNumber) {
          if (showError) this.error = "Please enter the packing slip number";
          return false;
        }
        if (!this.formData.packingSlipDate) {
          if (showError) this.error = "Please enter the packing slip date";
          return false;
        }
        if (this.formData.documents.length === 0) {
          if (showError)
            this.error = "Please upload the packing slip document";
          return false;
        }
        return true;

      case 3: // Confirm
        if (this.formData.damageReported && !this.formData.damageDescription) {
          if (showError)
            this.error = "Please describe the overall damage situation";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // ==================== Data Loading ====================

  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load plants
      if ((this.rootStore as any).plantsStore) {
        await (this.rootStore as any).plantsStore.find();
      }

      // Load POs pending receiving
      if ((this.rootStore as any).purchaseOrdersStore) {
        await (this.rootStore as any).purchaseOrdersStore.find({
          query: {
            status: "pending_receiving",
            $sort: { expectedDeliveryDate: 1 },
          },
        });
      }

      runInAction(() => {
        this.plants = (this.rootStore as any).plantsStore?.entities || [];
        const allPOs =
          (this.rootStore as any).purchaseOrdersStore?.entities || [];
        this.pendingPOs = allPOs.filter(
          (po: PurchaseOrder) =>
            po.status === "pending_receiving" || po.status === "partial_received"
        );
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  // ==================== Submission ====================

  generateReceivingNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `RCV-${year}${month}${day}-${random}`;
  };

  submitReceiving = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const receivingNumber = this.generateReceivingNumber();
      const po = this.formData.selectedPO!;

      // Update line items with received quantities
      const lineItemsStore = (this.rootStore as any).purchaseOrderLineItemsStore;
      if (lineItemsStore) {
        for (const item of this.formData.lineItems) {
          if (item.receivingQuantity > 0) {
            await lineItemsStore.patch(item.lineItemId, {
              receivedQuantity: item.receivedQuantity + item.receivingQuantity,
              status:
                item.receivedQuantity + item.receivingQuantity >= item.quantity
                  ? "received"
                  : "partial",
            });
          }
        }
      }

      // Update PO status
      const purchaseOrdersStore = (this.rootStore as any).purchaseOrdersStore;
      if (purchaseOrdersStore) {
        const allReceived = this.formData.lineItems.every(
          (item) =>
            item.receivedQuantity + item.receivingQuantity >= item.quantity
        );

        await purchaseOrdersStore.patch(po.purchaseOrderId, {
          status: allReceived ? "pending_approval" : "partial_received",
          hasPackingSlip: true,
          lastReceivedDate: new Date().toISOString().split("T")[0],
        });

        // If fully received, create approval queue task
        if (allReceived) {
          const approvalQueueStore = (this.rootStore as any).approvalQueueStore;
          if (approvalQueueStore) {
            await approvalQueueStore.create({
              queueType: "approval",
              entityType: "purchase_order",
              entityId: po.purchaseOrderId,
              entityNumber: po.poNumber,
              plantId: po.plantId,
              plantCode: po.plantCode,
              title: `Approve PO ${po.poNumber}`,
              description: `${po.vendorName} - ${this.receivingValue.toLocaleString(
                "en-US",
                { style: "currency", currency: "USD" }
              )}`,
              priority: po.priority === "urgent" ? "urgent" : "normal",
              status: "pending",
              createdDate: new Date().toISOString().split("T")[0],
              dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              vendorName: po.vendorName,
              totalAmount: po.totalAmount,
              receivingNumber: receivingNumber,
            });
          }
        }
      }

      runInAction(() => {
        this.success = `Receiving ${receivingNumber} submitted successfully!${
          this.isPartialReceipt ? " (Partial Receipt)" : ""
        }`;
        this.loading = false;

        // Navigate to receiving queue after delay
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("ReceivingQueue");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit receiving";
        this.loading = false;
      });
    }
  };

  // ==================== Reset ====================

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = { ...initialFormData, lineItems: [], documents: [] };
    this.plantFilter = null;
    this.vendorFilter = "";
    this.searchTerm = "";
  };
}

export default POReceivingWizardStore;
